import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SurveyResult } from './records/surveys/survey';
import { TrainingReport } from './records/training-report/training-report';
import { TrainingHoursReport } from './records/training-hours/training-hours';
import { LoginInactivityReport } from './records/login-inactivity/login-inactivity';
import { ModuleRatingData } from './records/module-ratings/module-ratings';
import { IUsersAndGroupsData } from './records/controls/student-group-filter/student-group-types';
import { CompetencyReport } from './records/competencies/competencies';
import { ModuleOption } from './learning-center-types';
import { QualificationsReport } from './records/qualifications/qualifications';
import { ExamAnalyticsHeatmapData, ExamAnalyticsHeatmapFilterData } from './records/records-types';
import { ExamScoresReport } from './records/exam-scores/exam-scores-report';
import { DateTime } from 'luxon';
import { ExamAnalyticsOverviewData } from './content-types';
//import * as _ from 'underscore';

@Injectable({
  providedIn: 'root'
})
export class RecordsService {
  //readonly saveUrl = '/supportrequest/submitsupportrequest';
  readonly surveyDataUrl = '/records/courses/surveys';
  readonly surveyListUrl = '/records/getSurveyList';
  readonly trainingReportDataUrl = '/records/students/trainingReport';
  readonly moduleListUrl = '/records/getModuleList';
  readonly moduleRatingsUrl = '/records/courses/getModuleRatings';
  readonly usersListUrl = '/records/getUsersAndGroups';
  readonly networkModuleUrl = '/records/getModulesData';
  readonly networkOlteData = '/records/getNetworkOlteNames';
  readonly usersModuleSessions = '/records/getModuleSessionsData';
  readonly studentsCourses = '/records/getStudentsCourses';
  readonly userOlteData = '/records/getOlteData';
  readonly loginInactivityData = '/records/getLoginInactivity';
  readonly getCompetenciesReportDataUrl = '/records/getCompetenciesReportData';
  readonly getExamScoresReportDataUrl = '/records/getExamScoresReportData';
  readonly getExamScoresReportFiltersUrl = '/records/getExamScoresReportFilters';
  readonly getExpressModulesListUrl = '/expressModule/getModuleList';
  readonly usersQualificationAssignments = '/records/getStudentsQualificationAssignments';
  readonly getExamAnalyticsHeatmapDataUrl = '/records/getExamAnalyticsHeatmapData';
  readonly getExamAnalyticsHeatmapFilterDataUrl = '/records/getExamAnalyticsHeatmapFilterData';
  readonly getAllExamAttemptsUrl = '/content/getAllExamAttempts';

  constructor(private http: HttpClient) { }

  getTrainingReportData() {
    return this.http.get<TrainingReport.TrainingData>(`${this.trainingReportDataUrl}`);
  }

  getSurveyData(survey: number) {
    return this.http.get<SurveyResult>(`${this.surveyDataUrl}?survey=${survey}`);
  }

  getModuleRatingsData() {
    return this.http.get<ModuleRatingData[]>(`${this.moduleRatingsUrl}`);
  }

  getSurveyList() {
    return this.http.get(this.surveyListUrl);
  }

  getModuleList() {
    return this.http.get(this.moduleListUrl);
  }

  getUsersData(includeStudentGroups: boolean, includeActiveUsers: boolean, includeArchivedUsers: boolean) {
    return this.http.post<IUsersAndGroupsData>(this.usersListUrl, { includeStudentGroups: includeStudentGroups, includeActiveUsers: includeActiveUsers, includeArchivedUsers: includeArchivedUsers });
  }

  getModulesData(networkID: number) {
    return this.http.post<TrainingHoursReport.ModulesData>(this.networkModuleUrl, {networkID: networkID});
  }

  getOlteData(networkID: number) {
    return this.http.post<TrainingHoursReport.OlteData>(this.networkOlteData, {networkID: networkID, type: "all"});
  }

  getStudentsCoursesData(networkID: number) {
    return this.http.post<TrainingHoursReport.StudentsCoursesData>(this.studentsCourses, {networkID: networkID});
  }

  getModuleSessionsData(data: TrainingHoursReport.FilterData) {
    return this.http.post<TrainingHoursReport.ModuleSessionsData>(this.usersModuleSessions, data);
  }

  getUserOlteData(data: TrainingHoursReport.FilterData) {
    return this.http.post<TrainingHoursReport.UserOlteData>(this.userOlteData, data);
  }

  getLoginInactivityData(data: LoginInactivityReport.FilterData) {
    return this.http.post<LoginInactivityReport.StudentsLastLoginSessionData>(this.loginInactivityData, data);
  }

  getCompetenciesReportData(filterData: CompetencyReport.FilterData) {
    return this.http.post<any>(this.getCompetenciesReportDataUrl, filterData);
  }

  getExamScoresReportFilters(data: ExamScoresReport.FiltersRequest = {}) {
    return this.http.post<ExamScoresReport.FiltersResponse>(this.getExamScoresReportFiltersUrl, data);
  }

  getExamScoresReportData(filterData: ExamScoresReport.FilterData) {
    return this.http.post<ExamScoresReport.ReportResponse>(this.getExamScoresReportDataUrl, filterData);
  }

  getStudentProfileData(userId)  {
    return this.http.post<any>('/users/getProfileData.json', {userId: userId});
  }

  getExpressModulesList() {
    return this.http.get<ModuleOption[]>(this.getExpressModulesListUrl);
  }

  getStudentsQualificationsData(data: QualificationsReport.FilterData) {
    return this.http.post<QualificationsReport.QualificationAssignmentsData>(this.usersQualificationAssignments, data);
  }

  getExamAnalyticsHeatmapData(data: ExamAnalyticsHeatmapFilterData) {
    return this.http.post<ExamAnalyticsHeatmapData[]>(this.getExamAnalyticsHeatmapDataUrl, data);
  }

  getExamAnalyticsHeatmapFilterData() {
    return this.http.get<ExamAnalyticsHeatmapFilterData[]>(this.getExamAnalyticsHeatmapFilterDataUrl);
  }

  getAllExamAttempts(fromDate: DateTime, toDate: DateTime) {
    return this.http.post<ExamAnalyticsOverviewData>(this.getAllExamAttemptsUrl, {fromDate: fromDate, toDate: toDate});
  }
}

let recordsServiceStub: Partial<RecordsService> = {};
export { recordsServiceStub };
